/**
 * Step00_Wait 화면단 — 위험성평가 워크플로 대기 대시보드
 * 로직·데이터는 WaitF.js
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Paper,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Campaign as CampaignIcon,
  School as SchoolIcon,
  WarningAmber as WarningIcon,
  Assignment as AssignmentIcon,
  ChatBubbleOutline as ChatIcon,
  CastForEducation as ResultEducationIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  BarChart as BarChartIcon,
  FilterList as FilterListIcon,
  MoreHoriz as MoreHorizIcon,
  TrendingUp as TrendingUpIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  WAIT_STEP_STATUS,
  ResolveWaitDepartmentRiskTableKind,
  NormalizeRsMethod,
  IsFrequencyIntensityMethod,
} from './Wait_Schema';

/** 완료·워크플로 아이콘·주요 버튼 */
const ACCENT_GREEN = '#2CA900';
const ACCENT_GREEN_LIGHT = '#E8F5E9';
/** 진행 중 단계·진행률 뱃지·막대(기획 파란 포인트) */
const ACCENT_BLUE = '#1976d2';
const ACCENT_BLUE_LIGHT = '#E3F2FD';
const WAIT_UI = {
  pageBg: '#f3f6fb',
  cardBorder: 'rgba(15, 23, 42, 0.08)',
  cardShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
  cardShadowHover: '0 14px 28px rgba(15, 23, 42, 0.11)',
};

/** Regulation.js 외곽·카드와 동일한 폭·배경 패턴 */
const pageShellSx = {
  p: { xs: 1.5, sm: 2 },
  width: '100%',
  bgcolor: WAIT_UI.pageBg,
  borderRadius: 2.5,
};

const mainCardSx = {
  width: '100%',
  borderRadius: 2.25,
  bgcolor: '#fff',
  borderColor: WAIT_UI.cardBorder,
  boxShadow: WAIT_UI.cardShadow,
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    boxShadow: WAIT_UI.cardShadowHover,
    transform: 'translateY(-1px)',
  },
};

const innerPanelSx = {
  width: '100%',
  borderRadius: 2.1,
  bgcolor: '#fff',
  borderColor: WAIT_UI.cardBorder,
  boxShadow: '0 6px 18px rgba(16, 24, 40, 0.06)',
};

const STEP_ICONS = {
  safetyInfo: ShieldIcon,
  notice: CampaignIcon,
  preTraining: SchoolIcon,
  riskAssessment: WarningIcon,
  improvement: AssignmentIcon,
  board: ChatIcon,
  resultTraining: ResultEducationIcon,
};
const STEP_ICONS_BY_SEQ = {
  1: ShieldIcon,
  2: CampaignIcon,
  3: SchoolIcon,
  4: WarningIcon,
  5: AssignmentIcon,
  6: ChatIcon,
  7: ResultEducationIcon,
};

const DASHBOARD = {
  chartDiscovery: '#4CAF50',
  chartImprovement: '#BBDEFB',
  kpiImprovement: '#4CAF50',
  kpiHighRisk: '#D32F2F',
  kpiRate: '#1976D2',
  tableLow: '#2E7D32',
  tableMedium: '#F57C00',
  tableHigh: '#E65100',
  tableVeryHigh: '#D32F2F',
  fiVeryLow: '#1B5E20',
  fiLow: '#2E7D32',
  fiNormal: '#F9A825',
  fiSlightlyHigh: '#FB8C00',
  fiHigh: '#E65100',
  fiVeryHigh: '#D32F2F',
};

const DefaultProgressDashboard = () => ({
  totalDiscoveries: 0,
  improvementActions: 0,
  highRiskCount: 0,
  improvementRatePercent: 0,
  improvementRateDeltaPercent: null,
  departmentChart: [],
  departmentRiskTable: { kind: 'fi', rows: [] },
});

const SplitDeptLabelLines = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return [''];
  if (text.length <= 8) return [text];
  const line1 = text.slice(0, 8);
  const rest = text.slice(8);
  if (rest.length <= 8) return [line1, rest];
  return [line1, `${rest.slice(0, 7)}…`];
};

const DepartmentXAxisTick = ({ x, y, payload }) => {
  const lines = SplitDeptLabelLines(payload?.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={18}
        textAnchor="middle"
        fill="#546E7A"
        fontSize={11}
      >
        {lines.map((line, idx) => (
          <tspan key={`${line}-${idx}`} x={0} dy={idx === 0 ? 0 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

function DashboardKpiCard({ label, value, valueColor, adornment }) {
  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 2,
        borderColor: 'rgba(15,23,42,0.08)',
        bgcolor: '#fff',
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            display="block"
            sx={{ mb: 0.75 }}
          >
            {label}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: valueColor, lineHeight: 1.2 }}
          >
            {value}
          </Typography>
        </Box>
        {adornment ? (
          <Box sx={{ flexShrink: 0, pt: 0.25 }}>{adornment}</Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function Wait({
  assessmentTitle = '',
  subtitle = '전체 공정의 안전성을 확보하기 위해 순차적으로 진행해주세요.',
  overallProgressPercent = 0,
  steps = [],
  rsMethod = '',
  progressDashboard: progressDashboardProp,
  loading = false,
  onBackToList,
  onStartNextStep,
  onStepEdit,
  onStepContinue,
}) {
  const progressDashboard = progressDashboardProp ?? DefaultProgressDashboard();
  const departmentRiskTable =
    progressDashboard.departmentRiskTable &&
    typeof progressDashboard.departmentRiskTable === 'object'
      ? progressDashboard.departmentRiskTable
      : { kind: ResolveWaitDepartmentRiskTableKind(rsMethod), rows: [] };
  const riskTableKind =
    departmentRiskTable.kind === 'threeLevel' ? 'threeLevel' : 'fi';
  const departmentRiskRows = Array.isArray(departmentRiskTable.rows)
    ? departmentRiskTable.rows
    : [];

  const inProgressStep = steps.find(
    (s) => s.status === WAIT_STEP_STATUS.inProgress
  );
  const workflowStatusText = inProgressStep
    ? `진행중: ${inProgressStep.title}`
    : '진행중: —';

  let assessmentMethodLabel = '평가방법: 미지정';
  if (NormalizeRsMethod(rsMethod)) {
    assessmentMethodLabel = IsFrequencyIntensityMethod(rsMethod)
      ? '평가방법: 빈도·강도법'
      : '평가방법: 위험성수준 3단계판단법';
  }

  return (
    <Box sx={{ ...pageShellSx, minHeight: '100%', boxSizing: 'border-box' }}>
      {/* 최상단: 워크플로 타이틀 + 진행 상태 */}
      <Box
        sx={{
          p: { xs: 1.2, sm: 1.4 },
          borderRadius: 2,
          border: '1px solid rgba(44,169,0,0.2)',
          background:
            'linear-gradient(90deg, rgba(232,245,233,0.9) 0%, rgba(244,250,245,0.98) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: ACCENT_GREEN,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography variant="h6" fontWeight={900} letterSpacing={-0.2}>
            위험성평가 워크플로우
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: 'text.secondary' }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: ACCENT_BLUE,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            {workflowStatusText}
          </Typography>
        </Stack>
      </Box>

      <Paper
        variant="outlined"
        elevation={0}
        sx={{
          ...mainCardSx,
          p: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {/* 위험성평가명 · 진행률 · 목록 · 다음 단계 — 단일 요약 카드 */}
        <Paper
          variant="outlined"
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 2,
            bgcolor: '#fff',
            borderColor: 'rgba(0,0,0,0.08)',
            boxShadow: '0 1px 4px rgba(16, 24, 40, 0.06)',
            p: { xs: 2, sm: 2.5 },
            mb: 3.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2.5}
            justifyContent="space-between"
            alignItems={{ lg: 'flex-start' }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                color="text.primary"
                sx={{
                  fontSize: { xs: '1.2rem', sm: '1.35rem' },
                  letterSpacing: '-0.02em',
                  mb: 0.75,
                }}
              >
                {assessmentTitle || '위험성평가'}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color="text.primary"
                sx={{ lineHeight: 1.5, mb: 0.75, letterSpacing: -0.01 }}
              >
                {assessmentMethodLabel}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.65 }}
              >
                {subtitle}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent={{ sm: 'flex-end' }}
              flexShrink={0}
            >
              <Chip
                icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
                label={`진행률 ${overallProgressPercent}%`}
                sx={{
                  height: 34,
                  bgcolor: ACCENT_BLUE_LIGHT,
                  color: ACCENT_BLUE,
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'rgba(44, 169, 0, 0.35)',
                  borderRadius: '17px',
                  '& .MuiChip-icon': { color: ACCENT_BLUE },
                }}
              />
              <Button
                variant="outlined"
                color="inherit"
                onClick={onBackToList}
                sx={{
                  textTransform: 'none',
                  minWidth: 88,
                  bgcolor: '#f8fafc',
                  borderColor: '#cbd5e1',
                  color: '#334155',
                  fontWeight: 700,
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f1f5f9',
                  },
                }}
              >
                목록
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onStartNextStep}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 2.5,
                  minHeight: 40,
                  letterSpacing: '0.01em',
                  background:
                    'linear-gradient(90deg, #217F00 0%, #2CA900 52%, #46B220 100%)',
                  boxShadow: '0 8px 18px rgba(44, 169, 0, 0.3)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background:
                      'linear-gradient(90deg, #1A6800 0%, #217F00 52%, #2CA900 100%)',
                    boxShadow: '0 10px 22px rgba(44, 169, 0, 0.38)',
                  },
                }}
              >
                다음 단계 시작하기
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* 워크플로 스테퍼 */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1.25, sm: 1.5 },
            width: '100%',
            overflowX: { xs: 'auto', md: 'visible' },
            pb: 2,
            mb: 3.5,
            scrollbarGutter: 'stable',
          }}
        >
          {steps.map((step) => {
            const seq = Number(step?.stepSeq ?? 0);
            const Icon =
              STEP_ICONS[step.routeStepId] ||
              STEP_ICONS[step.stepId] ||
              STEP_ICONS_BY_SEQ[seq] ||
              ShieldIcon;
            const isDone = step.status === WAIT_STEP_STATUS.completed;
            const isActive = step.status === WAIT_STEP_STATUS.inProgress;
            const isPending = step.status === WAIT_STEP_STATUS.pending;

            return (
              <Card
                key={String(step.stepId ?? step.routeStepId ?? `step-${seq}`)}
                elevation={0}
                sx={{
                  minWidth: { xs: 148, sm: 120 },
                  flex: { xs: '0 0 auto', md: '1 1 0' },
                  maxWidth: { md: 'none' },
                  border: isActive ? 3 : 1,
                  borderColor: isActive ? ACCENT_BLUE : '#e5e7eb',
                  bgcolor: isPending ? '#f8fafc' : '#fff',
                  borderRadius: 2,
                  opacity: isPending ? 0.92 : 1,
                  transition:
                    'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(15, 23, 42, 0.08)',
                    transform: 'translateY(-1px)',
                  },
                  ...(isActive && {
                    boxShadow: `0 0 0 1px ${ACCENT_BLUE_LIGHT}`,
                  }),
                }}
              >
                <CardContent sx={{ py: 2, px: 1.5, '&:last-child': { pb: 2 } }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}
                  >
                    {isDone ? (
                      <CheckCircleIcon
                        sx={{ fontSize: 40, color: ACCENT_GREEN }}
                      />
                    ) : (
                      <Icon
                        sx={{
                          fontSize: 40,
                          color: isActive ? ACCENT_BLUE : 'grey.400',
                        }}
                      />
                    )}
                  </Box>
                  {isActive && (
                    <Chip
                      label="진행중"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor: ACCENT_BLUE_LIGHT,
                        color: ACCENT_BLUE,
                        display: 'flex',
                        mx: 'auto',
                        mb: 1,
                      }}
                    />
                  )}
                  {isDone && (
                    <Chip
                      label="완료"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor: ACCENT_GREEN_LIGHT,
                        color: ACCENT_GREEN,
                        display: 'flex',
                        mx: 'auto',
                        mb: 1,
                      }}
                    />
                  )}
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    textAlign="center"
                    sx={{ mb: 0.5, lineHeight: 1.3 }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    textAlign="center"
                    sx={{ mb: 1, minHeight: 32, lineHeight: 1.35 }}
                  >
                    {step.subtitle}
                  </Typography>
                  {isDone && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => onStepEdit?.(step.stepId, step)}
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: ACCENT_GREEN,
                      }}
                    >
                      수정하기
                    </Link>
                  )}
                  {isActive && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => onStepContinue?.(step.stepId, step)}
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        fontWeight: 700,
                        textDecoration: 'none',
                        color: ACCENT_BLUE,
                      }}
                    >
                      계속하기 &gt;
                    </Link>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* 진행 현황 대시보드 */}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            ...innerPanelSx,
            width: '100%',
            p: { xs: 2, sm: 2.5 },
            mt: 0,
          }}
        >
          <Box
            component="header"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <BarChartIcon sx={{ fontSize: 26, color: ACCENT_GREEN }} />
              <Typography
                variant="subtitle1"
                fontWeight={800}
                letterSpacing={-0.2}
              >
                진행 현황
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 1fr',
                sm: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            <DashboardKpiCard
              label="총 발굴건수"
              value={progressDashboard.totalDiscoveries}
              valueColor="text.primary"
            />
            <DashboardKpiCard
              label="개선조치 건수"
              value={progressDashboard.improvementActions}
              valueColor={DASHBOARD.kpiImprovement}
            />
            <DashboardKpiCard
              label="고위험 항목"
              value={progressDashboard.highRiskCount}
              valueColor={DASHBOARD.kpiHighRisk}
              adornment={
                <ErrorOutlineIcon
                  sx={{ fontSize: 22, color: DASHBOARD.kpiHighRisk }}
                />
              }
            />
            <DashboardKpiCard
              label="개선율"
              value={`${Number(progressDashboard.improvementRatePercent).toFixed(1)}%`}
              valueColor={DASHBOARD.kpiRate}
              adornment={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <TrendingUpIcon
                    sx={{ fontSize: 22, color: DASHBOARD.kpiRate }}
                  />
                  {progressDashboard.improvementRateDeltaPercent != null && (
                    <Typography
                      component="span"
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: DASHBOARD.kpiRate }}
                    >
                      +
                      {Number(
                        progressDashboard.improvementRateDeltaPercent
                      ).toFixed(1)}
                      %
                    </Typography>
                  )}
                </Stack>
              }
            />
          </Box>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems="stretch"
            sx={{ width: '100%' }}
          >
            <Paper
              variant="outlined"
              elevation={0}
              sx={{
                flex: { lg: '1.65 1 0' },
                minWidth: 0,
                p: 2,
                borderRadius: 2,
                borderColor: 'rgba(15,23,42,0.08)',
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                부서별 통계
              </Typography>
              {progressDashboard.departmentChart.length === 0 && !loading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 6, textAlign: 'center' }}
                >
                  위험성평가 세부 데이터가 없습니다. 안전보건정보·위험성평가
                  실시를 저장하면 집계됩니다.
                </Typography>
              ) : (
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={progressDashboard.departmentChart}
                      margin={{ top: 8, right: 8, left: -8, bottom: 28 }}
                      barCategoryGap="18%"
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#ECEFF1"
                      />
                      <XAxis
                        dataKey="name"
                        tick={<DepartmentXAxisTick />}
                        interval={0}
                        tickMargin={12}
                        height={92}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#546E7A' }}
                        allowDecimals={false}
                      />
                      <Legend
                        wrapperStyle={{ paddingTop: 4 }}
                        formatter={(value) => (
                          <span
                            style={{
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#334155',
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                      <RechartsTooltip
                        formatter={(v, name) => [v, name]}
                        labelFormatter={(label) => String(label)}
                      />
                      <Bar
                        dataKey="discoveries"
                        name="발굴건수"
                        fill={DASHBOARD.chartDiscovery}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={28}
                      />
                      <Bar
                        dataKey="improvements"
                        name="개선조치"
                        fill={DASHBOARD.chartImprovement}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={28}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>

            <Paper
              variant="outlined"
              elevation={0}
              sx={{
                flex: { lg: '1 1 0' },
                minWidth: 0,
                p: 2,
                borderRadius: 2,
                borderColor: 'rgba(15,23,42,0.08)',
                overflow: 'auto',
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                부서별 요약
              </Typography>
              <TableContainer
                sx={{
                  maxHeight: 264, // 헤더 + 데이터 5행 고정 노출
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                  scrollbarGutter: 'stable',
                  '&::-webkit-scrollbar': {
                    width: 8,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#F1F5F9',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#94A3B8',
                    borderRadius: 8,
                  },
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': {
                      py: 0.75, // 행 높이 고정(5행 표시 기준)
                    },
                    '& .MuiTableCell-head': {
                      whiteSpace: 'nowrap',
                      wordBreak: 'keep-all',
                      fontSize: '0.82rem',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          bgcolor: '#fafafa',
                          width: riskTableKind === 'threeLevel' ? '34%' : '25%',
                        }}
                      >
                        부서명
                      </TableCell>
                      {riskTableKind === 'threeLevel' ? (
                        <>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            상
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            중
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            하
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            매우낮음
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            낮음
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            보통
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            약간 높음
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            높음
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: '#fafafa' }}
                          >
                            매우높음
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentRiskRows.length === 0 && !loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={riskTableKind === 'threeLevel' ? 4 : 7}
                        >
                          <Typography variant="body2" color="text.secondary">
                            표시할 부서별 요약이 없습니다.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : riskTableKind === 'threeLevel' ? (
                      departmentRiskRows.map((row) => (
                        <TableRow key={row.depNm}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {row.depNm}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: DASHBOARD.tableVeryHigh,
                              fontWeight: 700,
                            }}
                          >
                            {row.sang}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: DASHBOARD.tableMedium,
                              fontWeight: 700,
                            }}
                          >
                            {row.jung}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: DASHBOARD.tableLow, fontWeight: 700 }}
                          >
                            {row.ha}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      departmentRiskRows.map((row) => (
                        <TableRow key={row.depNm}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {row.depNm}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: DASHBOARD.fiVeryLow, fontWeight: 700 }}
                          >
                            {row.veryLow}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: DASHBOARD.fiLow, fontWeight: 700 }}
                          >
                            {row.low}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: DASHBOARD.fiNormal, fontWeight: 700 }}
                          >
                            {row.normal}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: DASHBOARD.fiSlightlyHigh,
                              fontWeight: 700,
                            }}
                          >
                            {row.slightlyHigh}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: DASHBOARD.fiHigh, fontWeight: 700 }}
                          >
                            {row.high}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: DASHBOARD.fiVeryHigh,
                              fontWeight: 700,
                            }}
                          >
                            {row.veryHigh}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </Paper>
      </Paper>
    </Box>
  );
}

export default Wait;
